import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Package, DollarSign, Truck, CheckCircle2, Clock, ToggleLeft, ToggleRight, Save } from "lucide-react";
import { api } from "@/lib/api";
import { Loading, PageHead, money } from "@/components/common";

function Stat({ icon: Icon, value, label, testid }) {
  return (
    <div className="ss-card stat-card" data-testid={testid}>
      <div className="stat-ic"><Icon size={22} /></div>
      <div><div className="stat-val">{value}</div><div className="stat-label">{label}</div></div>
    </div>
  );
}

export default function AdminShipping() {
  const [ov, setOv] = useState(null);
  const [settings, setSettings] = useState(null);
  const [hasKey, setHasKey] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => Promise.all([api.get("/shipping/admin/overview"), api.get("/shipping/settings")])
    .then(([o, s]) => { setOv(o); setSettings(s.settings); setHasKey(s.has_api_key); }).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!ov || !settings) return <Loading />;

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put("/shipping/settings", {
        markup: settings.markup, live: settings.live,
        max_weight_lb: Number(settings.max_weight_lb) || 70,
      });
      toast.success("Shipping settings updated");
      load();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  return (
    <div className="fade-up">
      <PageHead title="Shipping (Admin)" sub="Platform-wide shipping overview, revenue and configuration." />

      <div className="ss-grid grid-4" data-testid="admin-shipping-overview">
        <Stat icon={Package} value={ov.labeled} label={`Labels created / ${ov.total} orders`} testid="ship-stat-labeled" />
        <Stat icon={Clock} value={ov.awaiting} label="Awaiting shipment" testid="ship-stat-awaiting" />
        <Stat icon={CheckCircle2} value={ov.delivered} label="Delivered" testid="ship-stat-delivered" />
        <Stat icon={DollarSign} value={money(ov.markup_revenue)} label="Markup revenue" testid="ship-stat-markup" />
        <Stat icon={Truck} value={money(ov.carrier_cost)} label="Carrier cost (total)" />
        <Stat icon={DollarSign} value={money(ov.buyer_charged)} label="Buyers charged (shipping)" />
      </div>

      {/* Settings */}
      <div className="ss-card" style={{ padding: 22, marginTop: 20 }} data-testid="admin-shipping-settings">
        <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 16 }}>Configuration</div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
          <div>
            <label className="ss-label">Platform markup ($ flat per shipment)</label>
            <div className="ss-field" style={{ height: 44 }}>
              <DollarSign size={16} className="ss-muted" />
              <input type="number" min="0" step="0.5" value={settings.markup?.value ?? 2}
                onChange={(e) => setSettings({ ...settings, markup: { type: "fixed", value: Number(e.target.value) } })}
                data-testid="markup-input" />
            </div>
          </div>
          <div>
            <label className="ss-label">Max package weight (lb)</label>
            <div className="ss-field" style={{ height: 44 }}>
              <Package size={16} className="ss-muted" />
              <input type="number" min="1" value={settings.max_weight_lb}
                onChange={(e) => setSettings({ ...settings, max_weight_lb: e.target.value })} data-testid="maxweight-input" />
            </div>
          </div>
        </div>

        <div style={{ marginTop: 18, padding: 16, borderRadius: 12, background: "var(--gold-soft)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 8 }}>
              Live ShipEngine label purchases
              <span className={`ss-pill ${settings.live ? "pill--delivered" : "pill--ended"}`}>{settings.live ? "LIVE" : "Simulated"}</span>
            </div>
            <div className="ss-muted" style={{ fontSize: 13, marginTop: 4, maxWidth: 460 }}>
              {settings.live
                ? "Real labels will be purchased via ShipStation and cost real money."
                : "Rates & labels are simulated (deterministic). No carrier charges are incurred."}
              {!hasKey && " No API key configured."}
            </div>
          </div>
          <button className="ss-btn ss-btn-ghost" data-testid="live-toggle"
            disabled={!hasKey}
            onClick={() => {
              if (!settings.live) {
                if (!window.confirm("Enable LIVE mode? Real ShipStation labels will be purchased and will cost real money. You must also click 'Save configuration' to apply.")) return;
              }
              setSettings({ ...settings, live: !settings.live });
            }}>
            {settings.live ? <ToggleRight size={20} style={{ color: "var(--success)" }} /> : <ToggleLeft size={20} />}
            {settings.live ? "Disable live mode" : "Enable live mode"}
          </button>
        </div>

        <button className="ss-btn ss-btn-primary" style={{ marginTop: 18 }} onClick={saveSettings} disabled={saving} data-testid="save-admin-shipping">
          <Save size={16} /> {saving ? "Saving…" : "Save configuration"}
        </button>
      </div>

      {/* Recent shipments */}
      <h2 style={{ fontSize: 20, margin: "26px 0 12px" }}>Recent shipments</h2>
      {ov.recent.length === 0 ? (
        <div className="ss-muted" style={{ padding: 20 }}>No labels created yet.</div>
      ) : (
        <div className="ss-card" style={{ overflow: "auto" }}>
          <table className="ss-table" data-testid="admin-shipments-table">
            <thead><tr><th>Item</th><th>Buyer</th><th>Carrier</th><th>Tracking</th><th>Cost</th><th>Markup</th></tr></thead>
            <tbody>
              {ov.recent.map((o) => (
                <tr key={o.id}>
                  <td style={{ fontWeight: 700 }}>{o.item_title}</td>
                  <td>{o.buyer_name}</td>
                  <td>{o.carrier} {o.service}</td>
                  <td className="ss-muted" style={{ fontSize: 12 }}>{o.tracking_number}</td>
                  <td>{money(o.shipping_cost)}</td>
                  <td style={{ color: "var(--success)", fontWeight: 700 }}>{money(o.platform_markup)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
